import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import {
    Box,
    Button,
    IconButton,
    LinearProgress,
    Popover,
    Stack,
    TextField,
    Tooltip,
} from "@mui/material";
import { useAtom } from "jotai";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import {
    interviewAtom,
    setupDictionary,
} from "../../../stores/jotai/interviews/currentInterview";
import { updateTranscriptData } from "../../../util/transcriptionUpdateUtils";
import OverlayTitle from "../../generics/components/overlay-title";

export default function MaterialSpeakerModal(props) {
    const { slateData, transcriptDataURL } = props;
    const [anchorEl, setAnchorEl] = useState(null);
    const [speakers, setSpeakers] = useState([]);
    const [savingSpeakers, setSavingSpeakers] = useState(false);
    const [interviewState, setInterviewState] = useAtom(interviewAtom);
    const speakerDictionary = interviewState?.speakerDictionary || [];
    const speakerLibrary = interviewState?.speakerLibrary || [];

    const saveSpeaker = (
        oldSpeakerLibrary,
        newSpeakerDictionary,
        editorData
    ) => {
        setSavingSpeakers(true);

        updateTranscriptData({
            transcriptS3ObjectID: props.transcriptDataURL,
            data: {
                type: "transcript",
                data: {
                    speakerDictionary: newSpeakerDictionary,
                    speakerLibrary: oldSpeakerLibrary,
                    slate: editorData,
                },
            },
            onSuccess: () => {
                setupDictionary(newSpeakerDictionary, setInterviewState);
            },
            onFinish: () => {
                setSavingSpeakers(false);
            },
        });
    };

    const debouncedSaveSpeakerFn = useCallback(debounce(saveSpeaker, 500), []);

    const onChange = (speakerName, speakerIndex) => {
        const newSpeakerSet = [...speakers];

        newSpeakerSet[speakerIndex].name = speakerName;

        setSpeakers(newSpeakerSet);
        debouncedSaveSpeakerFn(speakerLibrary, newSpeakerSet, slateData);
    };

    useEffect(() => {
        setSpeakers([...speakerDictionary]);
    }, [speakerDictionary]);

    return (
        <>
            <Popover
                id={"material-speaker-modal-popover1"}
                open={!!anchorEl}
                anchorEl={anchorEl}
                onClose={() => {
                    setAnchorEl(null);
                }}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                slotProps={{
                    paper: {
                        sx: {
                            margin: "-8px 0 0 -8px",
                        },
                    },
                }}
            >
                <Box>
                    <Stack
                        direction="column"
                        sx={{ minWidth: "350px", width: "350px" }}
                    >
                        <OverlayTitle
                            title={
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    sx={{ alignItems: "center" }}
                                >
                                    <Box>Speaker Names</Box>{" "}
                                    {savingSpeakers && (
                                        <Box
                                            component="span"
                                            sx={{
                                                fontSize: "12px",
                                                opacity: 0.4,
                                            }}
                                        >
                                            Saving...
                                        </Box>
                                    )}
                                </Stack>
                            }
                            onClose={() => {
                                setAnchorEl(null);
                            }}
                        />
                        <Stack
                            spacing={3}
                            sx={{
                                padding: "16px",
                            }}
                        >
                            {speakers.map((speaker, index) => (
                                <TextField
                                    size="small"
                                    label={`Speaker ${index + 1}`}
                                    value={speaker.name}
                                    onChange={(event) =>
                                        onChange(event.target.value, index)
                                    }
                                />
                            ))}
                            {savingSpeakers && (
                                <LinearProgress
                                    sx={{
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        width: "100%",
                                        margin: "0 !important",
                                    }}
                                />
                            )}
                        </Stack>
                        <Stack
                            direction="row"
                            sx={{
                                padding: "0 16px 16px",
                            }}
                        >
                            <Button
                                variant="outlined"
                                sx={{ marginLeft: "auto" }}
                                onClick={() => {
                                    const speakerIndex = speakers.length;
                                    const speakerID = `spk_${speakerIndex}`;
                                    const newSpeakerSet = [
                                        ...speakers,
                                        {
                                            id: speakerID,
                                            name: speakerID,
                                        },
                                    ];

                                    
                                    setSpeakers(newSpeakerSet);
                                    saveSpeaker(speakerLibrary, newSpeakerSet, slateData);
                                }}
                            >
                                Add Speaker
                            </Button>
                        </Stack>
                    </Stack>
                </Box>
            </Popover>
            <Tooltip title="Manage transcription speaker labels">
                <IconButton
                    onClick={(event) => {
                        setAnchorEl(event.currentTarget);
                    }}
                >
                    <PersonOutlineOutlinedIcon />
                </IconButton>
            </Tooltip>
        </>
    );
}
